import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { SimulationForm } from './components/SimulationForm';
import { ResultsDisplay } from './components/ResultsDisplay';
import { AlgorithmTab } from './components/AlgorithmTab';
import SEOHead from './components/SEOHead';
import { SimulationRequest, SimulationResponse } from './types';

function App() {
  const [results, setResults] = useState<SimulationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'simulator' | 'algorithm'>('simulator');

  const handleSimulation = async (request: SimulationRequest) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use backend URL directly for local development
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/simulate'
        : '/api/simulate';
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: SimulationResponse = await response.json();
      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <HelmetProvider>
      <div className="App">
        <SEOHead 
          title="Retirement Withdrawal Simulator"
          description="Advanced retirement planning tool with Monte Carlo simulations and historical backtesting. Calculate safe withdrawal rates and analyze portfolio performance."
          keywords="retirement planning, Monte Carlo simulation, safe withdrawal rate, portfolio analysis, financial planning, retirement calculator, 4% rule"
        />
        
        <header className="App-header">
          <h1>🎯 Retirement Withdrawal Simulator</h1>
          <p>Analyze your retirement strategy with Monte Carlo and historical backtesting</p>
        </header>
        
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'simulator' ? 'active' : ''}`}
            onClick={() => setActiveTab('simulator')}
          >
            🎯 Simulator
          </button>
          <button 
            className={`tab-button ${activeTab === 'algorithm' ? 'active' : ''}`}
            onClick={() => setActiveTab('algorithm')}
          >
            🔬 Algorithm & Data
          </button>
        </div>

        <main className="App-main">
          {activeTab === 'simulator' && (
            <>
              <SimulationForm onSubmit={handleSimulation} loading={loading} />
              
              {loading && (
                <div className="loading">
                  <div className="spinner"></div>
                  <p>Running simulation...</p>
                </div>
              )}
              
              {error && (
                <div className="error">
                  <p>❌ Error: {error}</p>
                </div>
              )}
              
              {results && <ResultsDisplay results={results} />}
            </>
          )}
          
          {activeTab === 'algorithm' && <AlgorithmTab />}
        </main>
      </div>
    </HelmetProvider>
  );
}

export default App;